import { CalendarRange, CalendarSearch, Goal } from 'lucide-react';
import { useProfessionalCalendar } from '@/context/ProfessionalCalendarContext';
import {
	countRemainingVisibleSessionsOnDay,
	countRemainingVisibleSessionsInWeek,
	countUpcomingVisibleSessions,
} from '../utils/calendarLogic';

const formatTodayLabel = (count: number) => {
	const noun = count === 1 ? 'reunião' : 'reuniões';
	return `Hoje: ${count} ${noun} hoje`;
};

const formatWeekRemainingLabel = (count: number) =>
	`Semana: ${count} restantes essa semana`;

const formatUpcomingLabel = (count: number) => {
	const noun = count === 1 ? 'consulta futura' : 'consultas futuras';
	return `Total: ${count} ${noun}`;
};

const summaryIconClass = 'size-3.5 shrink-0 md:size-4';

type ProfessionalHomeSummaryFooterProps = {
	sessions: Sessions.Session[];
	isLoading: boolean;
};

const ProfessionalHomeSummaryFooter = ({
	sessions,
	isLoading,
}: ProfessionalHomeSummaryFooterProps) => {
	const { now } = useProfessionalCalendar();

	const todayCount = countRemainingVisibleSessionsOnDay(sessions, now, now);
	const weekRemainingCount = countRemainingVisibleSessionsInWeek(
		sessions,
		now,
	);
	const upcomingCount = countUpcomingVisibleSessions(sessions, now);

	return (
		<footer className="hidden shrink-0 border-t border-zinc-200 bg-zinc-50/90 px-4 py-1.5 md:px-6 min-[900px]:block">
			<div className="flex flex-nowrap items-center justify-between gap-x-4 overflow-hidden">
				<div className="flex min-w-0 flex-nowrap items-center gap-x-4 overflow-hidden md:gap-x-6">
					<span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm font-bold tabular-nums leading-none text-zinc-400">
						<Goal className={summaryIconClass} aria-hidden />
						{isLoading ? (
							<span>Carregando resumo…</span>
						) : (
							<span>{formatTodayLabel(todayCount)}</span>
						)}
					</span>

					<span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm font-bold tabular-nums leading-none text-zinc-400">
						<CalendarRange
							className={summaryIconClass}
							aria-hidden
						/>
						{isLoading ? (
							<span>—</span>
						) : (
							<span>
								{formatWeekRemainingLabel(weekRemainingCount)}
							</span>
						)}
					</span>
				</div>

				<span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm font-bold tabular-nums leading-none text-zinc-400">
					<CalendarSearch className={summaryIconClass} aria-hidden />
					{isLoading ? (
						<span>—</span>
					) : (
						<span>{formatUpcomingLabel(upcomingCount)}</span>
					)}
				</span>
			</div>
		</footer>
	);
};

export { ProfessionalHomeSummaryFooter };
